import { useEffect, useMemo, useState } from 'react';
import { Title, useCreatePath } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { clientDayKey, dateTimeNoSeconds, warsawParts } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { MOCKUP_SEVERITY_ATTR } from '../../mockupLook/tokens';
import {
  AVIZATION_CHANNELS,
  AVIZATION_STATES,
  FEATURE_CLIENT_AVIZATIONS,
  fetchAvizations,
  filterFromSearch,
  toErrorMessage,
  type Avization,
} from './clientCommunicationApi';

/** Adres bieżącej listy (`#/client-avizations?filter=…`) — filtr z kafelka pulpitu. */
export function currentSearch(): string {
  const hash = window.location.hash ?? '';
  const i = hash.indexOf('?');
  return i >= 0 ? hash.slice(i) : window.location.search;
}

const stateColor = (a: Avization): 'error' | 'warning' | 'success' | 'default' => {
  if (a.state === 'todo') return a.overdue ? 'error' : 'warning';
  if (a.state === 'done') return 'success';
  return 'default';
};

/** Chip stanu: „do wykonania”, „po terminie — przypomnienie”, „wykonana”… */
export const AvizationStateChip = ({ a }: { a: Avization }) => (
  <Chip
    size="small"
    color={stateColor(a)}
    label={a.state === 'todo' && a.overdue ? 'po terminie — przypomnienie' : a.stateLabel}
    data-testid={`aviz-state-${a.id}`}
  />
);

/**
 * Kolejka awizacji (makieta p. Mariusza: „Kolejka awizacji”; prace 26.10.2026,
 * pakiet P1; przełącznik `clientAvizations`, S-26.10-14): zadania według
 * terminu, kanał, stan, wykonawca. Po terminie (parametr przypomnienia) —
 * czerwony chip. Zadania powstają z reguły klienta, ręcznie przy zleceniu
 * i jako ponowne po zmianie zlecenia.
 */
export const AvizationQueueScreen = () => {
  const on = useFeatureState(FEATURE_CLIENT_AVIZATIONS);
  const createPath = useCreatePath();
  const initial = useMemo(() => filterFromSearch(currentSearch()), []);
  const [state, setState] = useState(typeof initial.state === 'string' ? initial.state : 'todo');
  const [channel, setChannel] = useState(typeof initial.channel === 'string' ? initial.channel : '');
  const [data, setData] = useState<{ items: Avization[]; reminderMin: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (on !== true) return;
    let alive = true;
    fetchAvizations({ state, channel })
      .then((r) => {
        if (!alive) return;
        setData(r);
        setError(null);
      })
      .catch((e) => alive && setError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
  }, [on, state, channel]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  return (
    <Box sx={{ p: 1 }} data-testid="aviz-queue">
      <Title title="Kolejka awizacji" />
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Awizacje według terminu. Powstają z reguły awizacji klienta (karta kontrahenta), są zakładane przez CS przy
            zleceniu albo wracają jako ponowne po zmianie pojazdu, terminu lub miejsca.
            {data ? ` Przypomnienie: ${data.reminderMin} min po terminie.` : ''}
          </Typography>
          <Box sx={{ display: 'grid', gap: 1, mb: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))' }}>
            <TextField
              select
              size="small"
              label="Stan"
              value={state}
              onChange={(e) => setState(e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'aviz-queue-state' } }}
            >
              {AVIZATION_STATES.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Kanał"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'aviz-queue-channel' } }}
            >
              <MenuItem value="">wszystkie</MenuItem>
              {AVIZATION_CHANNELS.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {data && data.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="aviz-queue-empty">
              Brak awizacji w tym widoku. Bez reguły klienta zadania zakłada CS na karcie zlecenia (sekcja 7).
            </Typography>
          ) : null}
          {data && data.items.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Termin</TableCell>
                  <TableCell>Zlecenie</TableCell>
                  <TableCell>Klient · punkt</TableCell>
                  <TableCell>Kanał</TableCell>
                  <TableCell>Stan</TableCell>
                  <TableCell>Wykonawca</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((a) => (
                  // Kontrakt `data-testid` paczki: wiersz kolejki awizacji.
                  <TableRow
                    key={a.id}
                    data-testid={`cs-awizacje-wiersz-${a.id}`}
                    data-state={a.state}
                    // Wygląd z makiety (partia 2): awizacja po terminie z czerwonym paskiem jak `.sev.b` makiety.
                    {...{ [MOCKUP_SEVERITY_ATTR]: a.state === 'todo' && a.overdue ? 'error' : undefined }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeNoSeconds(a.dueAt)}</TableCell>
                    <TableCell>
                      <Link to={createPath({ resource: 'client-avizations', type: 'show', id: a.id })} data-testid={`aviz-open-${a.id}`}>
                        {a.orderNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {a.clientName ?? '—'}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {a.pointLabel}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip size="small" variant="outlined" label={a.channelLabel} />
                        {a.origin === 'repeat' ? (
                          <Chip size="small" color="info" label={`ponowna: ${a.originNote ?? 'zmiana'}`} />
                        ) : null}
                        {a.origin === 'rule' ? <Chip size="small" variant="outlined" label="z reguły klienta" /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <AvizationStateChip a={a} />
                      {a.attempts > 0 && a.state === 'todo' ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          prób: {a.attempts}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{a.doneByName ?? a.createdByName ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

/** Godziny kalendarza (makieta: 08–15) — wcześniejsze i późniejsze trafiają na brzegi. */
export const CALENDAR_HOURS = [8, 9, 10, 11, 12, 13, 14, 15];

/** Godzina polska terminu, przycięta do zakresu kalendarza. */
export function calendarHour(iso: string): number {
  const h = warsawParts(new Date(iso)).hour;
  return Math.min(Math.max(h, CALENDAR_HOURS[0]), CALENDAR_HOURS[CALENDAR_HOURS.length - 1]);
}

/**
 * Kalendarz telefonów (makieta p. Mariusza: „Kalendarz awizacji
 * telefonicznych” — szkic, „przebieg wymaga warsztatu z wykonawcami”):
 * awizacje telefoniczne dnia według godziny, z rozmówcą i punktem;
 * „zadzwoń →” otwiera awizację. Dzień polski.
 */
export const AvizationCalendarScreen = () => {
  const on = useFeatureState(FEATURE_CLIENT_AVIZATIONS);
  const createPath = useCreatePath();
  const [day, setDay] = useState(() => clientDayKey(new Date()));
  const [items, setItems] = useState<Avization[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (on !== true) return;
    let alive = true;
    fetchAvizations({ state: 'all', channel: 'phone', day })
      .then((r) => {
        if (!alive) return;
        setItems(r.items.filter((a) => a.state !== 'superseded'));
        setError(null);
      })
      .catch((e) => alive && setError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
  }, [on, day]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  return (
    <Box sx={{ p: 1 }} data-testid="aviz-calendar">
      <Title title="Kalendarz telefonów" />
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Awizacje telefoniczne dnia według godziny (czas polski). Wcześniejsze terminy są przy 8:00, późniejsze przy
            15:00. Przebieg awizacji telefonicznych do ustalenia z działem — to widok pomocniczy.
          </Typography>
          <TextField
            size="small"
            type="date"
            label="Dzień"
            value={day}
            onChange={(e) => e.target.value && setDay(e.target.value)}
            sx={{ mb: 1 }}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'aviz-calendar-day' } }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: '64px 1fr', rowGap: 0.5, columnGap: 1 }}>
            {CALENDAR_HOURS.map((h) => {
              const calls = (items ?? []).filter((a) => calendarHour(a.dueAt) === h);
              return (
                <Box key={h} sx={{ display: 'contents' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, pt: 0.5 }}>
                    {`${String(h).padStart(2, '0')}:00`}
                  </Typography>
                  <Box
                    sx={{ borderTop: 1, borderColor: 'divider', minHeight: 32, py: 0.5 }}
                    data-testid={`aviz-calendar-slot-${h}`}
                  >
                    {calls.map((a) => (
                      <Box
                        key={a.id}
                        data-testid={`aviz-calendar-call-${a.id}`}
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          opacity: a.state === 'todo' ? 1 : 0.6,
                        }}
                      >
                        <Typography variant="body2">
                          <b>{a.contactName ?? 'rozmówca nieznany'}</b> · {a.pointLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dateTimeNoSeconds(a.dueAt).slice(-5)} · {a.orderNumber} · {a.clientName ?? '—'}
                        </Typography>
                        <AvizationStateChip a={a} />
                        <Link to={createPath({ resource: 'client-avizations', type: 'show', id: a.id })}>zadzwoń →</Link>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {items && items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="aviz-calendar-empty" sx={{ mt: 1 }}>
              Tego dnia nie ma awizacji telefonicznych.
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};
